(() => {
  'use strict';

  if (window.BandenmeestersNewClient) return;

  const BASE_PATH = '/apps/bm-auto/api/v1';

  class BmNewClientError extends Error {
    constructor(message, { status = 0, code = '', retryAfter = '', payload = null } = {}) {
      super(message);
      this.name = 'BmNewClientError';
      this.status = status;
      this.code = code;
      this.retryAfter = retryAfter;
      this.payload = payload;
    }
  }

  const asText = (value) => typeof value === 'string' ? value : '';

  // UNPUBLISHED, signed-preview-only navigation to an exact read-back Shopify handle.
  // This is not the public native vehiclePageUrl and is never saved as one.
  const unpublishedVehiclePagePreviewUrl = (type, { themeRole = '' } = {}) => {
    const handle = asText(type?.vehiclePreviewHandle);
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle)) return null;
    try {
      const current = new URL(window.location.href);
      const previewHost = current.hostname === 'shopifypreview.com'
        || current.hostname.endsWith('.shopifypreview.com');
      if (!previewHost && !['unpublished', 'development'].includes(asText(themeRole).toLowerCase())) return null;
      const target = new URL(`/pages/voertuigen/${handle}`, current.origin);
      if (!previewHost) {
        const themeId = current.searchParams.get('preview_theme_id') || '';
        if (/^\d+$/.test(themeId)) target.searchParams.set('preview_theme_id', themeId);
      }
      return target.href;
    } catch (_) { return null; }
  };

  // Shopify supplies the only allowed canonical product origin; previews keep their own host.
  const shopOrigin = (() => {
    const configured = typeof document === 'undefined' ? '' : document.currentScript?.dataset?.bmShopUrl;
    if (!asText(configured)) return '';
    try {
      const url = new URL(configured);
      return url.protocol === 'https:' && !url.username && !url.password && !url.search && !url.hash && url.pathname === '/'
        ? url.origin : '';
    } catch (_) { return ''; }
  })();


  const requireObject = (value, label) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new BmNewClientError(`Invalid Bridge ${label} response`, { code: 'invalid_response' });
    }
    return value;
  };

  const requireArray = (value, label) => {
    if (!Array.isArray(value)) {
      throw new BmNewClientError(`Invalid Bridge ${label} response`, { code: 'invalid_response' });
    }
    return value;
  };

  const validateDirectoryRows = (rows, kind) => requireArray(rows, kind).map((row) => {
    requireObject(row, kind);
    if (!asText(row.ref) || !asText(row.label)) {
      throw new BmNewClientError(`Invalid Bridge ${kind} row`, { code: 'invalid_response' });
    }
    return row;
  });

  const validateTypeRows = (rows) => requireArray(rows, 'types').map((row) => {
    requireObject(row, 'types');
    if (!asText(row.vehicleId) || !asText(row.label)) {
      throw new BmNewClientError('Invalid Bridge types row', { code: 'invalid_response' });
    }
    return row;
  });

  const validateTechnicalAttributes = (value, label) => {
    if (value === null || value === undefined) return null;
    const attrs = requireObject(value, label);
    const allowedConstruction = [null, 'ONE_PIECE', 'TWO_PIECE', 'MULTI_PIECE'];
    const allowedProcess = [null, 'CAST', 'FLOW_FORMED', 'FORGED'];
    const allowedBoreType = [null, 'FIXED', 'FLEXIBLE_DOWN', 'DRILLABLE_UP', 'NO_CENTER_BORE'];

    if (!allowedConstruction.includes(attrs.constructionForm ?? null)) {
      throw new BmNewClientError(`Invalid Bridge ${label}.constructionForm`, { code: 'invalid_response' });
    }
    if (!allowedProcess.includes(attrs.manufacturingProcess ?? null)) {
      throw new BmNewClientError(`Invalid Bridge ${label}.manufacturingProcess`, { code: 'invalid_response' });
    }
    if (![null, true, false].includes(attrs.winterSuitable ?? null)) {
      throw new BmNewClientError(`Invalid Bridge ${label}.winterSuitable`, { code: 'invalid_response' });
    }
    if (!allowedBoreType.includes(attrs.centerBoreType ?? null)) {
      throw new BmNewClientError(`Invalid Bridge ${label}.centerBoreType`, { code: 'invalid_response' });
    }
    if (!Array.isArray(attrs.productApprovalRefs ?? [])) {
      throw new BmNewClientError(`Invalid Bridge ${label}.productApprovalRefs`, { code: 'invalid_response' });
    }
    return attrs;
  };

  const validateFitmentFacts = (row) => {
    for (const key of ['supplierMatch', 'bmCheck']) {
      if (row[key] === undefined || row[key] === null) continue;
      const fact = requireObject(row[key], key);
      if (!asText(fact.state) || (fact.label !== null && fact.label !== undefined && typeof fact.label !== 'string')) invalid(key);
      const states = key === 'supplierMatch' ? ['MATCHED', 'BLOCKED', 'NOT_CONFIRMED'] : ['VERIFIED', 'NOT_CHECKED'];
      if (!states.includes(fact.state)) invalid(key);
    }
    if (row.bmCheck?.state === 'VERIFIED' && row.supplierMatch?.state !== 'MATCHED') invalid('unbound expert check');
    return row;
  };
  const hasFitmentVeto = row => row?.supplierMatch?.state === 'BLOCKED'
    || row?.fitment?.status === 'BLOCKED'
    || ['FAILED', 'CONFLICT', 'BLOCKED'].includes(row?.bmCheck?.state);
  const hasSupplierMatch = row => row?.supplierMatch?.state === 'MATCHED' && !hasFitmentVeto(row);

  const validateSetOption = (option) => {
    requireObject(option, 'setOption');
    if (!asText(option.setId) || option.setType !== 'SQUARE' || option.defaultQuantity !== 4) {
      throw new BmNewClientError('Invalid Bridge setOption identity', { code: 'invalid_response' });
    }
    if (!Number.isFinite(Number(option.diameterIn))) {
      throw new BmNewClientError('Invalid Bridge setOption diameter', { code: 'invalid_response' });
    }
    // P12 authorizes a bounded client request, not purchase/supplier orderability.
    // Legacy responses remain readable during the independent Bridge release.
    if (option.quantityRequest !== undefined) {
      const request = requireObject(option.quantityRequest, 'setOption.quantityRequest');
      if (request.contract !== 'BM_PHYSICAL_WHEEL_QUANTITY_REQUEST_V1'
        || request.unit !== 'PHYSICAL_WHEEL'
        || request.defaultQuantity !== option.defaultQuantity
        || request.minQuantity !== 1 || request.maxQuantity !== 20
        || request.authorization !== 'PER_QUANTITY_QUOTE_REQUIRED') {
        invalid('setOption.quantityRequest');
      }
    }
    const items = requireArray(option.items, 'setOption.items');
    if (!items.length) {
      throw new BmNewClientError('Invalid Bridge setOption items', { code: 'invalid_response' });
    }
    requireObject(option.shopifyVariantBinding, 'setOption.shopifyVariantBinding');
    // OPTIONAL_CANONICAL_PRICE_V1 keeps an exact published product/set informational
    // when the authoritative price is unknown; never use Shopify/DRAFT price as fallback.
    if (option.unitPrice !== null) {
      const price = requireObject(option.unitPrice, 'setOption.unitPrice');
      if (price.currency !== 'EUR' || !Number.isSafeInteger(price.amountCents) || price.amountCents <= 0) {
        invalid('setOption.unitPrice');
      }
    }
    requireObject(option.availability, 'setOption.availability');
    validateTechnicalAttributes(option.technicalAttributes, 'setOption.technicalAttributes');
    validateFitmentFacts(option);
    return option;
  };

  const validateProductCatalog = (payload, productRef) => {
    if (
      payload.market !== 'BE' ||
      payload.productRef !== productRef ||
      payload.productContract !== 'WHEEL_DESIGN_SET_OPTIONS_V1'
    ) {
      throw new BmNewClientError('Invalid Bridge product catalog envelope', { code: 'invalid_response' });
    }

    if (payload.product === null) return payload;
    const product = requireObject(payload.product, 'product');
    if (
      product.shopifyProductRef !== productRef ||
      !asText(product.manufacturer) ||
      !asText(product.designName) ||
      !asText(product.finish) ||
      !asText(product.productSummary).trim()
    ) {
      throw new BmNewClientError('Invalid Bridge product identity', { code: 'invalid_response' });
    }

    requireArray(product.setOptions, 'product.setOptions').forEach(validateSetOption);
    requireArray(product.sizeOptions, 'product.sizeOptions');
    if (asText(product.productUrl)) product.productUrl = customerUrl(product.productUrl);
    if (product.siblingFinishes !== null) {
      requireArray(product.siblingFinishes, 'product.siblingFinishes').forEach((sibling) => {
        if (asText(sibling?.productUrl)) sibling.productUrl = customerUrl(sibling.productUrl);
      });
    }
    validateTechnicalAttributes(product.sharedTechnicalAttributes, 'product.sharedTechnicalAttributes');
    return payload;
  };

  const request = async (path, { method = 'GET', body = null, signal = undefined, keepalive = false } = {}) => {
    const headers = { Accept: 'application/json' };
    const init = {
      method,
      credentials: 'same-origin',
      headers,
      signal,
      keepalive,
    };

    if (body !== null) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }

    let response;
    try {
      response = await fetch(`${BASE_PATH}${path}`, init);
    } catch (error) {
      throw new BmNewClientError('Bridge tijdelijk niet bereikbaar', {
        code: 'network_error',
        payload: error,
      });
    }

    const retryAfter = response.headers.get('Retry-After') || '';
    let payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      throw new BmNewClientError('Ongeldige Bridge-response', {
        status: response.status,
        code: 'invalid_json',
        retryAfter,
      });
    }

    if (!response.ok || payload?.ok === false) {
      const code = asText(payload?.error) || asText(payload?.reason) || `http_${response.status}`;
      throw new BmNewClientError(code, {
        status: response.status,
        code,
        retryAfter,
        payload,
      });
    }

    return requireObject(payload, 'payload');
  };

  // Two safe GET attempts only on a transient failure. Never retries a cart,
  // Quote, supplier order or a deliberate 400/404/409 refusal.
  const retryExactRead = async (path, { signal } = {}) => {
    try {
      return await request(path, { signal });
    } catch (error) {
      if (signal?.aborted || !(error?.code === 'network_error'
          || [500, 502, 503, 504].includes(error?.status))) throw error;
      await new Promise(resolve => setTimeout(resolve, 250));
      if (signal?.aborted) throw error;
      return request(path, { signal });
    }
  };

  const status = () => request('/status');
  const fitmentCoverage = payload => {
    const coverage = requireObject(payload.fitmentCoverage, 'fitment coverage');
    if (coverage.contract !== 'VEHICLE_FITMENT_SEARCH_V1'
      || !['KNOWN_MATCHES', 'NOT_RESEARCHED', 'PENDING', 'TEMPORARILY_UNAVAILABLE', 'PROVEN_NO_MATCHES'].includes(coverage.status)
      || !['COMPLETE', 'PARTIAL'].includes(coverage.coverage)) invalid('fitment coverage state');
    for (const field of ['matchedSkus', 'checkedSkus', 'scopedSkus']) {
      if (!Number.isSafeInteger(coverage[field]) || coverage[field] < 0) invalid('fitment coverage count');
    }
    if (coverage.matchedSkus > coverage.checkedSkus || coverage.checkedSkus > coverage.scopedSkus
      || (coverage.status === 'PROVEN_NO_MATCHES' && (coverage.coverage !== 'COMPLETE'
        || coverage.matchedSkus !== 0 || coverage.checkedSkus !== coverage.scopedSkus || !coverage.scopedSkus))) invalid('unproven empty fitment');
    return coverage;
  };
  const fitmentSearches = new Map();
  const fitmentSearchQuery = vehicleId => {
    if (!uuid(vehicleId)) invalid('vehicleId');
    return new URLSearchParams({ market: 'BE', vehicleId });
  };
  // Explicit exact selection or retry CTA only. Restore and reads never dispatch.
  const searchVehicleFitment = ({ vehicleId }) => {
    const query = fitmentSearchQuery(vehicleId), key = query.toString();
    if (!fitmentSearches.has(key)) {
      const pending = request(`/new/results?${query}`, { method: 'POST', keepalive: true })
        .then(payload => { if (payload.vehicleId !== vehicleId) invalid('search vehicle'); return fitmentCoverage(payload); })
        .finally(() => fitmentSearches.delete(key));
      fitmentSearches.set(key, pending);
    }
    return fitmentSearches.get(key);
  };
  // One status reader per Vehicle, shared by sections. Disposing the final
  // subscriber stops polling; late responses can never update another auto.
  const fitmentWatchers = new Map();
  const watchVehicleFitment = ({ vehicleId, signal, onUpdate, onError }) => {
    const query = fitmentSearchQuery(vehicleId), key = query.toString(); query.set('view', 'status');
    let watcher = fitmentWatchers.get(key);
    if (!watcher) {
      watcher = { listeners: new Set(), timer: null, attempts: 0, stopped: false };
      fitmentWatchers.set(key, watcher);
      const poll = async () => {
        if (watcher.stopped) return;
        if (document.hidden) { watcher.timer = setTimeout(poll, 30000); return; }
        try {
          const payload = await request(`/new/results?${query}`);
          if (watcher.stopped) return;
          if (payload.vehicleId !== vehicleId) invalid('status vehicle');
          const coverage = fitmentCoverage(payload);
          for (const listener of [...watcher.listeners]) listener.onUpdate(coverage);
          if (watcher.stopped) return;
          if (coverage.status === 'PENDING') {
            ++watcher.attempts;
            watcher.timer = setTimeout(poll, watcher.attempts < 6 ? 1500 + watcher.attempts * 500 : watcher.attempts < 20 ? 10000 : 30000); return;
          }
        } catch (error) {
          if (!watcher.stopped) for (const listener of [...watcher.listeners]) listener.onError?.(error);
        }
        watcher.stopped = true; if (fitmentWatchers.get(key) === watcher) fitmentWatchers.delete(key);
      };
      watcher.timer = setTimeout(poll, 1500);
    }
    const listener = { onUpdate, onError };
    const stop = () => {
      signal?.removeEventListener('abort', stop); watcher.listeners.delete(listener);
      if (!watcher.listeners.size) {
        watcher.stopped = true; clearTimeout(watcher.timer); if (fitmentWatchers.get(key) === watcher) fitmentWatchers.delete(key);
      }
    };
    if (signal?.aborted) { stop(); return stop; }
    watcher.listeners.add(listener); signal?.addEventListener('abort', stop, { once: true });
    return stop;
  };

  const invalid = (label) => { throw new BmNewClientError(`Invalid Bridge ${label}`, { code: 'invalid_response' }); };
  const customerUrl = (value, media = false) => {
    if (!asText(value)) invalid('customer URL');
    let url;
    try { url = new URL(value); } catch (_) { invalid('customer URL'); }
    if (url.protocol !== 'https:' || url.username || url.password
      || (media ? url.hostname !== 'cdn.shopify.com' : (url.origin !== location.origin && url.origin !== shopOrigin))) invalid('customer URL');
    // Exact Bridge URL path/identity, but do not let a draft-preview link escape to the live shop.
    return !media && url.origin === shopOrigin && url.origin !== location.origin
      ? `${location.origin}${url.pathname}${url.search}${url.hash}` : value;
  };

  const shopifyImageUrl = (source, width) => {
    if (!Number.isSafeInteger(width) || width < 16 || width > 2048) invalid('image width');
    const url = new URL(customerUrl(source, true));
    url.searchParams.set('width', String(width));
    return url.href;
  };
  const validateFamilyPage = (payload, vehicleId) => {
    if (vehicleId && payload.fitmentCoverage !== undefined) fitmentCoverage(payload);
    const contract = vehicleId ? payload.contract : payload.listingContract;
    if (contract !== 'DESIGN_FAMILY_LISTING_V1' || (vehicleId && payload.vehicleId !== vehicleId)
      || (!vehicleId && payload.market !== 'BE')) invalid('family envelope');
    for (const key of ['total', 'offset', 'limit']) {
      if (!Number.isSafeInteger(payload[key]) || payload[key] < (key === 'limit' ? 1 : 0)) invalid(`family ${key}`);
    }
    if (payload.limit > 60 || !['BRAND_DESIGN_ASC', 'PRICE_ASC', 'PRICE_DESC'].includes(payload.sort)) invalid('family pagination');
    const rows = requireArray(payload.results, 'families');
    if (rows.length > payload.limit) invalid('family page size');
    rows.forEach((family) => {
      requireObject(family, 'family');
      if (!asText(family.designFamilyKey) || !asText(family.manufacturer) || !asText(family.designName)) invalid('family identity');
      if ((!vehicleId && family.fitmentState !== 'NOT_CHECKED') || family.fitmentState === 'BLOCKED') invalid('family fitment');
      const identity = (product) => {
        requireObject(product, 'family product');
        if (!/^[1-9]\d*$/.test(product.shopifyProductRef || '') || !asText(product.wheelDesignId)) invalid('family product identity');
        product.productUrl = customerUrl(product.productUrl);
        customerUrl(product.primaryImageUrl, true);
      };
      identity(family.primaryProduct);
      const finishes = requireArray(family.finishOptions, 'family finishes');
      if (!finishes.length) invalid('family finishes');
      finishes.forEach((finish) => {
        identity(finish);
        if (!asText(finish.finish)) invalid('family finish');
        const sets = requireArray(finish.setOptions, 'family sets');
        sets.forEach(validateSetOption);
        if (vehicleId && (!sets.length || !sets.every(hasSupplierMatch))) invalid('family supplier match');
      });
      if (!finishes.some(f => f.shopifyProductRef === family.primaryProduct.shopifyProductRef && f.productUrl === family.primaryProduct.productUrl)) invalid('family primary finish');
      if (!requireArray(family.sizeOptions, 'family sizes').every(n => Number.isInteger(n) && n >= 10 && n <= 30)) invalid('family sizes');
      if (family.fromSetPrice4 !== null) {
        const price = requireObject(family.fromSetPrice4, 'family price');
        if (price.currency !== 'EUR' || !Number.isSafeInteger(price.amountCents) || price.amountCents <= 0) invalid('family price');
      }
    });
    const facets = requireObject(payload.facets, 'family facets');
    for (const name of ['manufacturer', 'design', 'finish', 'diameter']) requireArray(facets[name], `facet ${name}`);
    return payload;
  };

  const optionalPriceContract = 'OPTIONAL_CANONICAL_PRICE_V1';
  const checkedPriceContract = value => {
    if (value === undefined || value === '') return '';
    if (value !== optionalPriceContract) invalid('price contract');
    return value;
  };

  const familyListing = async ({ vehicleId = '', filters = {}, signal, priceContract } = {}) => {
    if (vehicleId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(vehicleId)) {
      throw new BmNewClientError('vehicleId is verplicht', { code: 'invalid_vehicle_id' });
    }
    const query = new URLSearchParams({ market: 'BE', view: 'family' });
    const acceptedPriceContract = checkedPriceContract(priceContract);
    if (acceptedPriceContract) query.set('priceContract', acceptedPriceContract);
    const allowed = ['manufacturer', 'designName', 'finish', 'diameter', 'search', 'sort', 'minPriceCents', 'maxPriceCents', 'offset', 'limit'];
    for (const [key, value] of Object.entries(filters)) {
      if (!allowed.includes(key)) throw new BmNewClientError('Onbekend filter', { code: 'invalid_filter' });
      for (const item of Array.isArray(value) ? value : [value]) query.append(key, String(item));
    }
    if (vehicleId) query.set('vehicleId', vehicleId);
    return validateFamilyPage(await request(`/new/${vehicleId ? 'results' : 'catalog'}?${query}`, { signal }), vehicleId);
  };

  const vehicleBrands = async ({ signal } = {}) => {
    const payload = await request('/vehicles/brands', { signal });
    validateDirectoryRows(payload.brands, 'brands');
    return payload;
  };

  const vehicleModels = async (brandRef, { signal } = {}) => {
    if (!asText(brandRef)) throw new BmNewClientError('brandRef is verplicht', { code: 'invalid_brand_ref' });
    const query = new URLSearchParams({ brandRef });
    const payload = await request(`/vehicles/models?${query}`, { signal });
    validateDirectoryRows(payload.models, 'models');
    return payload;
  };

  const vehicleTypes = async (brandRef, modelRef, { signal } = {}) => {
    if (!asText(brandRef)) throw new BmNewClientError('brandRef is verplicht', { code: 'invalid_brand_ref' });
    if (!asText(modelRef)) throw new BmNewClientError('modelRef is verplicht', { code: 'invalid_model_ref' });
    const query = new URLSearchParams({ brandRef, modelRef });
    const payload = await request(`/vehicles/types?${query}`, { signal });
    validateTypeRows(payload.types);
    // Both vehicle selectors show recent generations first.
    // Keep Bridge Vehicle UUIDs and equal-year ordering unchanged.
    const endYear = type => type.yearTo ? Number(type.yearTo) || 0 : 9999;
    payload.types = [...payload.types].sort((a, b) =>
      (Number(b.yearFrom) || 0) - (Number(a.yearFrom) || 0)
      || endYear(b) - endYear(a));
    return payload;
  };

  const productCatalog = async (productRef, { signal, priceContract } = {}) => {
    const normalizedProductRef = String(productRef || '').trim();
    if (!/^[1-9]\d*$/.test(normalizedProductRef)) {
      throw new BmNewClientError('productRef is verplicht', { code: 'invalid_product_ref' });
    }

    const query = new URLSearchParams({ market: 'BE', productRef: normalizedProductRef });
    const acceptedPriceContract = checkedPriceContract(priceContract);
    if (acceptedPriceContract) query.set('priceContract', acceptedPriceContract);
    const payload = await retryExactRead(`/new/catalog?${query}`, { signal });
    return validateProductCatalog(payload, normalizedProductRef);
  };

  const productFitment = async (productRef, vehicleId, { signal } = {}) => {
    const normalizedProductRef = String(productRef || '').trim();
    const normalizedVehicleId = asText(vehicleId).trim();
    if (!/^[1-9]\d*$/.test(normalizedProductRef)) {
      throw new BmNewClientError('productRef is verplicht', { code: 'invalid_product_ref' });
    }
    if (!normalizedVehicleId) {
      throw new BmNewClientError('vehicleId is verplicht', { code: 'invalid_vehicle_id' });
    }

    const query = new URLSearchParams({ market: 'BE', vehicleId: normalizedVehicleId });
    const payload = await retryExactRead(
      `/new/products/${encodeURIComponent(normalizedProductRef)}/fitment?${query}`,
      { signal },
    );

    if (
      payload.productRef !== normalizedProductRef ||
      payload.vehicleId !== normalizedVehicleId ||
      !['SELLABLE', 'COMPATIBLE', 'REVIEW_REQUIRED', 'BLOCKED'].includes(payload.status) ||
      !Array.isArray(payload.matchingSets)
    ) {
      throw new BmNewClientError('Invalid Bridge product fitment response', { code: 'invalid_response' });
    }
    const matches = requireArray(payload.setMatches, 'set matches');
    const seen = new Set();
    matches.forEach(row => {
      requireObject(row, 'set match');
      if (!uuid(row.setId) || seen.has(row.setId)) invalid('set match identity');
      seen.add(row.setId);
      validateFitmentFacts(row);
    });
    return payload;
  };

  const uuid = value => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value || '');
  const validatePhysicalConfiguration = (payload, setId) => {
    if (
      payload.productContract !== 'WHEEL_DESIGN_SET_OPTIONS_V1' ||
      payload.purchaseContext !== 'CATALOG_DIRECT' ||
      payload.fitmentState !== 'NOT_CHECKED' ||
      payload.checkoutEnabled !== false
    ) invalid('physical configuration envelope');

    const configuration = requireObject(payload.physicalConfiguration, 'physicalConfiguration');
    if (configuration.setId !== setId || !uuid(configuration.wheelDesignId)
      || !['SQUARE', 'STAGGERED'].includes(configuration.configuration)) {
      invalid('physical configuration identity');
    }

    const items = requireArray(configuration.items, 'physicalConfiguration.items');
    const validateItem = (item) => {
      requireObject(item, 'physicalConfiguration.item');
      if (!uuid(item.wheelSkuId) || !['ALL', 'FRONT', 'REAR'].includes(item.position)
        || !Number.isSafeInteger(item.quantity) || item.quantity < 1
        || !/^[1-9]\d*$/.test(String(item.shopifyVariantId || ''))) {
        invalid('physical configuration item');
      }
      return item;
    };
    items.forEach(validateItem);

    const validSquare = configuration.configuration === 'SQUARE'
      && items.length === 1 && items[0].position === 'ALL' && items[0].quantity === 4;
    const validStaggered = configuration.configuration === 'STAGGERED'
      && items.length === 2
      && items.some(item => item.position === 'FRONT' && item.quantity === 2)
      && items.some(item => item.position === 'REAR' && item.quantity === 2)
      && new Set(items.map(item => item.wheelSkuId)).size === 2
      && new Set(items.map(item => String(item.shopifyVariantId))).size === 2;
    if (!validSquare && !validStaggered) invalid('physical configuration topology');
    return payload;
  };

  const exactSet = async (setId, { signal } = {}) => {
    const normalizedSetId = asText(setId).trim();
    if (!uuid(normalizedSetId)) {
      throw new BmNewClientError('setId is verplicht', { code: 'invalid_set_id' });
    }
    const payload = await request(`/new/catalog/${encodeURIComponent(normalizedSetId)}?market=BE`, { signal });
    return validatePhysicalConfiguration(payload, normalizedSetId);
  };

  const validateVisualizationFrames = (value, label) => {
    const seenOrders = new Set();
    const seenUrls = new Set();
    return requireArray(value, label).map((frame) => {
      requireObject(frame, label);
      if (!Number.isSafeInteger(frame.order) || frame.order < 0
        || seenOrders.has(frame.order) || seenUrls.has(frame.url)) {
        invalid(label);
      }
      seenOrders.add(frame.order);
      seenUrls.add(frame.url);
      safeHttpsMediaUrl(frame.url);
      return frame;
    });
  };

  const cents = value => Number.isSafeInteger(value) && value > 0;
  const tyreMediaUrl = value => {
    const u = new URL(value);
    if (u.protocol !== 'https:' || u.username || u.password || u.port || u.hash ||
        u.hostname !== 'cdn.shopify.com') invalid('tyre media');
    return u.href;
  };
  const tyreSizes = async ({ signal } = {}) => {
    const payload = await request('/tyres?sizes=true', { signal });
    const sizes = requireArray(payload.sizes, 'tyre sizes');
    if (!['FRESH', 'UNAVAILABLE'].includes(payload.freshness) || sizes.length > 2000) invalid('tyre sizes');
    for (const s of sizes) {
      if (!Number.isInteger(s.widthMm) || s.widthMm < 100 || s.widthMm > 500 ||
          !Number.isInteger(s.aspectRatio) || s.aspectRatio < 20 || s.aspectRatio > 100 ||
          !Number.isInteger(s.diameterIn) || s.diameterIn < 10 || s.diameterIn > 30) invalid('tyre size');
    }
    return payload;
  };
  const tyres = async (filters, { signal } = {}) => {
    const payload = await request(`/tyres?${new URLSearchParams(Object.entries(filters).filter(([,v]) => v !== undefined && v !== '').map(([k,v]) => [k,String(v)]))}`, { signal });
    const addon = Boolean(filters.vehicleId || filters.wheelSkuId || filters.setId || filters.suggestions);
    const splitCandidate = id => Array.isArray(payload.configurations) && payload.configurations.some(c => c.front && c.rear &&
      ['widthMm','aspectRatio','diameterIn'].some(k => c.front[k] !== c.rear[k]) &&
      ['frontTyreSkuIds','rearTyreSkuIds'].some(k => c.candidates?.[k]?.includes(id)));
    requireArray(payload.items, 'tyres').forEach(t => {
      if (!uuid(t.id) || !asText(t.manufacturer) || !asText(t.profile) || ![t.widthMm,t.aspectRatio,t.diameterIn].every(Number.isInteger) ||
          !cents(t.unitRetailPriceCents) || t.fourPiecePriceCents !== t.unitRetailPriceCents * 4 ||
          !/^[1-9]\d*$/.test(t.shopifyVariantId || '') || !Number.isInteger(t.availablePieces) || t.availablePieces < (addon && splitCandidate(t.id) ? 2 : 4) || t.freshness !== 'FRESH' ||
          !Array.isArray(t.oemMarkings) || t.oemMarkings.some(v => !asText(v))) invalid('tyre');
      tyreMediaUrl(t.imageUrl);
      if (t.brandLogoUrl) tyreMediaUrl(t.brandLogoUrl);
      requireObject(t.label, 'tyre label');
      for (const url of [t.label.officialLabelUrl, t.label.productInformationUrl].filter(Boolean)) {
        const parsed = new URL(url);
        // Bridge validates official document origins. The consumer checks safe
        // navigation without owning a supplier-specific document allowlist.
        if (parsed.protocol !== 'https:' || parsed.username || parsed.password || parsed.port) invalid('tyre label URL');
      }
    });
    if (payload.nextCursor !== null && !uuid(payload.nextCursor)) invalid('tyre cursor');
    const configurations = requireArray(payload.configurations, 'tyre configurations');
    if (configurations.length > 32) invalid('tyre configurations');
    if (addon && configurations.length) {
      if (!['vehicleId','wheelSkuId','setId'].every(key => uuid(filters[key]) && payload.context?.[key] === filters[key]) ||
          !/^[a-f0-9]{64}$/.test(payload.context?.configurationFingerprint || '') ||
          payload.fitment?.state !== 'CONFIRMED' || payload.suggestions !== undefined) invalid('exact tyre configuration binding');
      for (const c of configurations) {
        for (const axle of [c.front,c.rear]) if (!axle || ![axle.widthMm,axle.aspectRatio,axle.diameterIn].every(Number.isInteger)) invalid('tyre axle');
        for (const key of ['frontTyreSkuIds','rearTyreSkuIds']) if (!Array.isArray(c.candidates?.[key]) ||
          c.candidates[key].some(id => !uuid(id) || !payload.items.some(t => t.id === id && t.availablePieces >=
            (['widthMm','aspectRatio','diameterIn'].some(k => c.front[k] !== c.rear[k]) ? 2 : 4)))) invalid('tyre candidates');
      }
    }
    if (payload.suggestions !== undefined) {
      const s = requireObject(payload.suggestions, 'wheel tyre suggestions');
      if (s.vehicleFitment !== 'NOT_CONFIRMED' || s.bmCheck !== 'NOT_CHECKED') invalid('suggestion claims');
      const axles = requireArray(s.axles, 'suggestion axles');
      if (axles.length < 1 || axles.length > 2 || new Set(axles.map(a => a.position)).size !== axles.length ||
          !(axles.length === 1 ? axles[0].position === 'ALL' : axles.some(a => a.position === 'FRONT') && axles.some(a => a.position === 'REAR'))) invalid('suggestion axles');
      for (const axle of axles) {
        if (!uuid(axle.wheelSkuId) || !Number.isFinite(axle.wheel?.widthIn) || axle.wheel.widthIn <= 0 ||
            !Number.isInteger(axle.wheel?.diameterIn) || !['FRESH','UNAVAILABLE'].includes(axle.freshness)) invalid('suggestion wheel');
        const sizes = requireArray(axle.sizes, 'suggestion sizes');
        if (sizes.length > 2000) invalid('suggestion sizes');
        for (const t of sizes) {
          if (!Number.isInteger(t.widthMm) || t.widthMm < 100 || t.widthMm > 500 ||
              !Number.isInteger(t.aspectRatio) || t.aspectRatio < 20 || t.aspectRatio > 100 || t.diameterIn !== axle.wheel.diameterIn ||
              !['MOUNTING_CONFIRMED','WIDTH_CANDIDATE','DIAMETER_ONLY','NOT_CONFIRMED'].includes(t.evidenceLevel)) invalid('suggestion size');
          if (['MOUNTING_CONFIRMED','WIDTH_CANDIDATE'].includes(t.evidenceLevel) && (!asText(t.sourceRef) || !asText(t.referenceVersion))) invalid('sizing evidence');
        }
      }
    } else if (filters.suggestions === 'true') invalid('wheel tyre suggestions');
    return payload;
  };
  const physicalQuote = async (intent, { signal } = {}) => {
    if (!uuid(intent.requestId) || !Array.isArray(intent.lines) || !intent.lines.length || intent.lines.length > 3 ||
        intent.lines.some(l => !uuid(l.skuId) || !Number.isInteger(l.quantity) || l.quantity < 1 || l.quantity > 20 || !['WHEEL','TYRE'].includes(l.kind) || !['ALL','FRONT','REAR'].includes(l.position))) invalid('physical intent');
    const payload = await request('/quotes', { method: 'POST', signal, body: { ...intent, market: 'BE', countryCode: 'BE' } });
    const lines = requireArray(payload.lines, 'physical quote lines');
    if (lines.length !== intent.lines.length || payload.currency !== 'EUR' || payload.market !== 'BE' || !cents(payload.retailPriceCents) ||
        !/^[A-Za-z0-9_-]{1,128}$/.test(payload.quotePublicId || '') || !Number.isFinite(Date.parse(payload.expiresAt)) || Date.parse(payload.expiresAt) <= Date.now()) invalid('physical quote');
    for (const l of lines) {
      const expected = intent.lines.filter(i => i.skuId === l.skuId && i.kind === l.kind);
      if (expected.length !== 1 || l.quantity !== expected[0].quantity || !/^[1-9]\d*$/.test(l.shopifyVariantId || '') || !cents(l.unitRetailPriceCents) || l.lineTotalCents !== l.unitRetailPriceCents * l.quantity) invalid('physical quote line');
    }
    if (new Set(lines.map(l => l.shopifyVariantId)).size !== lines.length || lines.reduce((n,l) => n + l.lineTotalCents,0) !== payload.retailPriceCents) invalid('physical quote total');
    return { ...payload, physical: true };
  };
  const quote = async ({ requestId, vehicleId, setId, postalCode, deliveryAddress, quantity = 4 }, { signal } = {}) => {
    if (!uuid(requestId) || !uuid(vehicleId) || !uuid(setId) || !/^[1-9][0-9]{3}$/.test(postalCode || '')) {
      throw new BmNewClientError('Controleer auto, configuratie en Belgische postcode', { code: 'invalid_request' });
    }
    if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 20) invalid('quote quantity');
    if (!deliveryAddress || ['recipientName', 'addressLine1', 'city', 'phone'].some(key =>
      typeof deliveryAddress[key] !== 'string' || !deliveryAddress[key].trim())) throw new Error('delivery_address_required');
    const address = Object.fromEntries(['recipientName', 'addressLine1', 'addressLine2', 'city', 'phone']
      .filter(key => typeof deliveryAddress[key] === 'string' && deliveryAddress[key].trim())
      .map(key => [key, deliveryAddress[key].trim()]));
    const payload = await request('/quotes', { method: 'POST', signal,
      body: { requestId, vehicleId, setId, postalCode, deliveryAddress: address, market: 'BE', countryCode: 'BE', quantity } });
    if (!/^[A-Za-z0-9_-]{1,128}$/.test(payload.quotePublicId || '') || payload.currency !== 'EUR'
      || payload.shopifyQuantity !== quantity || !cents(payload.retailPriceCents) || !cents(payload.unitRetailPriceCents)
      || payload.retailPriceCents !== payload.unitRetailPriceCents * quantity
      || !Number.isFinite(Date.parse(payload.expiresAt)) || Date.parse(payload.expiresAt) <= Date.now()) invalid('quote');
    const lines = requireArray(payload.lines, 'quote lines');
    if (lines.length !== 1 || lines[0].quantity !== quantity || !/^gid:\/\/shopify\/ProductVariant\/[1-9]\d*$/.test(lines[0].shopifyVariantGid || '')
      || lines[0].shopifyVariantGid !== payload.shopifyVariantGid || lines[0].unitRetailPriceCents !== payload.unitRetailPriceCents
      || lines[0].lineTotalCents !== payload.retailPriceCents) invalid('quote lines');
    if (!Number.isFinite(Date.parse(payload.deliveryPromiseLatestAt))) invalid('quote delivery');
    return payload;
  };
  const cartReference = async (currentQuote, { signal } = {}) => {
    if (!/^[A-Za-z0-9_-]{1,128}$/.test(currentQuote?.quotePublicId || '') || !Number.isFinite(Date.parse(currentQuote?.expiresAt)) || Date.parse(currentQuote.expiresAt) <= Date.now()) invalid('expired quote');
    const payload = await request('/cart-reference', { method: 'POST', signal, body: { quotePublicId: currentQuote.quotePublicId } });
    const lines = requireArray(payload.lines, 'cart reference lines');
    if (payload.quotePublicId !== currentQuote.quotePublicId || lines.length !== currentQuote.lines.length) invalid('cart reference');
    lines.forEach((line, index) => {
      const quoted = currentQuote.physical ? currentQuote.lines.find(l => l.shopifyVariantId === line.shopifyVariantId) : currentQuote.lines[index];
      if (!quoted || line.shopifyVariantId !== (currentQuote.physical ? quoted.shopifyVariantId : quoted.shopifyVariantGid.split('/').pop()) || line.shopifyQuantity !== quoted.quantity
        || line.unitRetailPriceCents !== quoted.unitRetailPriceCents || line.lineTotalCents !== quoted.lineTotalCents
        || line.lineProperty?.name !== '_bm_ref' || !asText(line.lineProperty.value).trim()) invalid('cart reference line');
    });
    if (new Set(lines.map(l => l.shopifyVariantId)).size !== lines.length || payload.retailPriceCents !== currentQuote.retailPriceCents ||
        (!currentQuote.physical && (!asText(payload.vehicleDisplayLabel) || !asText(payload.setLabel)))) invalid('cart reference total');
    return payload;
  };

  window.BandenmeestersNewClient = Object.freeze({
    basePath: BASE_PATH,
    Error: BmNewClientError,
    status,
    familyListing,
    searchVehicleFitment,
    watchVehicleFitment,
    shopifyImageUrl,
    vehicleBrands,
    vehicleModels,
    vehicleTypes,
    unpublishedVehiclePagePreviewUrl,
    productCatalog,
    productFitment,
    hasSupplierMatch,
    hasFitmentVeto,
    exactSet,
    quote,
    cartReference,
    tyres,
    tyreSizes,
    physicalQuote,
  });
})();
